"use client";

import { c as _c } from "react/compiler-runtime";
import { Check, ExternalLink, Heart, Link2, Star } from "lucide-react";
import type { Dictionary } from "@/dictionaries/types";
import type { Collection } from "@/features/collections/types";
import { useCopy } from "../data/useCopy";
import { useLikeWatch } from "../data/useLikeWatch";
import { SOCIAL_ICON, XIcon } from "../icons";
import type { DetailModel } from "../model";
type Labels = Dictionary["collectionPage"];
const SOCIAL_LABEL = {
  website: "linkWebsite",
  x: "linkTwitter",
  instagram: "linkInstagram",
  telegram: "linkTelegram",
  discord: "linkDiscord"
} as const;
export type ActionsLook = "cover" | "nav";

/**
 * THE COLLECTION'S ACTIONS — like (with its count) and watchlist, then the
 * ways out: share on X, copy the link, the explorer, and the collection's
 * own links. One component, two mounts (see `ActionsPlacement`): the cover's
 * wall label and the pinned chapter bar; the controls and behaviour are the
 * same in both.
 */
export default function VitrineActions(t0) {
  const $ = _c(50);
  const {
    collection,
    m,
    t,
    isFa,
    look
  } = t0;
  const num = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US");
  const {
    like,
    watch
  } = useLikeWatch(collection, isFa);
  const link = useCopy(m.pageUrl, t.linkCopied);
  const t1 = "vt-actions";
  const t2 = "group";
  const t3 = t.identityActions;
  const t4 = "button";
  const t5 = "vt-act vt-act--like v2-focus";
  const t6 = like.toggle;
  const t7 = like.isPending;
  const t8 = like.liked;
  const t9 = like.isPending || undefined;
  const t10 = like.liked ? t.unlike : t.like;
  let t11;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Heart aria-hidden={true} />;
    $[0] = t11;
  } else {
    t11 = $[0];
  }
  const t12 = num.format(like.count);
  let t13;
  if ($[1] !== t12) {
    t13 = <b>{t12}</b>;
    $[1] = t12;
    $[2] = t13;
  } else {
    t13 = $[2];
  }
  let t14;
  if ($[3] !== like.isPending || $[4] !== like.liked || $[5] !== like.toggle || $[6] !== t10 || $[7] !== t11 || $[8] !== t13 || $[9] !== t9) {
    t14 = <button type={t4} className={t5} onClick={t6} disabled={t7} aria-pressed={t8} aria-busy={t9} aria-label={t10}>{t11}{t13}</button>;
    $[3] = like.isPending;
    $[4] = like.liked;
    $[5] = like.toggle;
    $[6] = t10;
    $[7] = t11;
    $[8] = t13;
    $[9] = t9;
    $[10] = t14;
  } else {
    t14 = $[10];
  }
  const t15 = watch.isPending || undefined;
  const t16 = watch.watchlisted ? t.removeFromWatchlist : t.addToWatchlist;
  const t17 = isFa ? "\u0648\u0627\u0686\u200C\u0644\u06CC\u0633\u062A" : "Watchlist";
  let t18;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Star aria-hidden={true} />;
    $[11] = t18;
  } else {
    t18 = $[11];
  }
  let t19;
  if ($[12] !== t15 || $[13] !== t16 || $[14] !== t17 || $[15] !== watch.isPending || $[16] !== watch.toggle || $[17] !== watch.watchlisted) {
    t19 = <button type="button" className="vt-act vt-act--watch v2-focus" onClick={watch.toggle} disabled={watch.isPending} aria-pressed={watch.watchlisted} aria-busy={t15} aria-label={t16} title={t17}>{t18}</button>;
    $[12] = t15;
    $[13] = t16;
    $[14] = t17;
    $[15] = watch.isPending;
    $[16] = watch.toggle;
    $[17] = watch.watchlisted;
    $[18] = t19;
  } else {
    t19 = $[18];
  }
  let t20;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <span className="vt-act-rule" aria-hidden={true} />;
    $[19] = t20;
  } else {
    t20 = $[19];
  }
  let t21;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <XIcon />;
    $[20] = t21;
  } else {
    t21 = $[20];
  }
  let t22;
  if ($[21] !== m.shareOnX || $[22] !== t.shareOnX) {
    t22 = <a className="vt-act vt-act--icon v2-focus" href={m.shareOnX} target="_blank" rel="noopener noreferrer" aria-label={t.shareOnX} title={t.shareOnX}>{t21}</a>;
    $[21] = m.shareOnX;
    $[22] = t.shareOnX;
    $[23] = t22;
  } else {
    t22 = $[23];
  }
  const t23 = link.copied ? t.linkCopied : t.copyLink;
  const t24 = link.copied || undefined;
  let t25;
  if ($[24] !== link.copied) {
    t25 = link.copied ? <Check aria-hidden={true} /> : <Link2 aria-hidden={true} />;
    $[24] = link.copied;
    $[25] = t25;
  } else {
    t25 = $[25];
  }
  let t26;
  if ($[26] !== link.copy || $[27] !== t.copyLink || $[28] !== t23 || $[29] !== t24 || $[30] !== t25) {
    t26 = <button type="button" className="vt-act vt-act--icon v2-focus" onClick={link.copy} aria-label={t.copyLink} title={t23} data-copied={t24}>{t25}</button>;
    $[26] = link.copy;
    $[27] = t.copyLink;
    $[28] = t23;
    $[29] = t24;
    $[30] = t25;
    $[31] = t26;
  } else {
    t26 = $[31];
  }
  let t27;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <ExternalLink aria-hidden={true} />;
    $[32] = t27;
  } else {
    t27 = $[32];
  }
  let t28;
  if ($[33] !== m.explorer || $[34] !== t.viewOnExplorer) {
    t28 = <a className="vt-act vt-act--icon v2-focus" href={m.explorer} target="_blank" rel="noopener noreferrer" aria-label={t.viewOnExplorer} title={t.viewOnExplorer}>{t27}</a>;
    $[33] = m.explorer;
    $[34] = t.viewOnExplorer;
    $[35] = t28;
  } else {
    t28 = $[35];
  }
  let t29;
  if ($[36] !== m.socials || $[37] !== t) {
    let t30;
    if ($[39] !== t) {
      t30 = t31 => {
        const {
          key,
          href
        } = t31;
        const Icon = SOCIAL_ICON[key];
        const label = t[SOCIAL_LABEL[key]];
        return <a key={key} className="vt-act vt-act--icon vt-act--social v2-focus" href={href} target="_blank" rel="noopener noreferrer" aria-label={label} title={label}><Icon /></a>;
      };
      $[39] = t;
      $[40] = t30;
    } else {
      t30 = $[40];
    }
    t29 = m.socials.map(t30);
    $[36] = m.socials;
    $[37] = t;
    $[38] = t29;
  } else {
    t29 = $[38];
  }
  let t30;
  if ($[41] !== look || $[42] !== t.identityActions || $[43] !== t14 || $[44] !== t19 || $[45] !== t22 || $[46] !== t26 || $[47] !== t28 || $[48] !== t29) {
    t30 = <div className={t1} data-look={look} role={t2} aria-label={t3}>{t14}{t19}{t20}{t22}{t26}{t28}{t29}</div>;
    $[41] = look;
    $[42] = t.identityActions;
    $[43] = t14;
    $[44] = t19;
    $[45] = t22;
    $[46] = t26;
    $[47] = t28;
    $[48] = t29;
    $[49] = t30;
  } else {
    t30 = $[49];
  }
  return t30;
}